import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, Subject, tap, map } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken?: string;
  user: User;
}

export interface User {
  id: number;
  companyId: number;
  companyName?: string; // Nome da empresa do usuário
  name: string;
  email: string;
  role: string;
  phoneE164?: string;
  whatsappNumber?: string;
  isActive?: boolean;
  createdAt?: string;
}

export interface UserProfile {
  id: number;
  companyId: number;
  companyName?: string; // Nome da empresa (para exibição)
  name: string;
  email: string;
  passwordHash?: string;
  role: string;
  roleId: number;
  phoneE164?: string;
  whatsappNumber?: string;
  timezone?: string;
  avatarUrl?: string; // URL do avatar/foto do usuário
}

export interface UpdateUserProfileRequest {
  name: string;
  phoneE164?: string;
  whatsappNumber?: string;
  timezone?: string;
  avatarUrl?: string; // URL do avatar/foto do usuário
}

export interface RegisterRequest {
  companyName: string;
  document: string; // CNPJ (14 dígitos) ou KVK (8 dígitos), apenas números
  documentType?: 'cnpj' | 'kvk'; // Indica ao backend como validar: KVK (Holanda) ou CNPJ (Brasil)
  phone: string; // Telefone E.164 (ex: +5511987654321 ou +31612345678)
  userName: string;
  email: string;
  password: string;
  locale?: string; // Locale atual do usuário (ex: 'pt', 'en', 'nl') para envio do email de boas-vindas
  // plan removido - trial é iniciado separadamente após registro
}

export interface RegisterResponse {
  token: string;
  companyId: number;
  companyName: string;
  user: User;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordConfirmRequest {
  email: string;
  code: string;
  newPassword: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly TOKEN_KEY = 'pipeup_token';
  private readonly REFRESH_TOKEN_KEY = 'pipeup_refresh_token';
  private readonly USER_KEY = 'pipeup_user';
  private readonly COMPANY_NAME_KEY = 'pipeup_company_name';

  private currentUserSubject = new BehaviorSubject<User | null>(
    this.getUserFromStorage()
  );
  public currentUser$ = this.currentUserSubject.asObservable();

  // Subject para notificar quando o perfil é atualizado (ex: avatar)
  private profileUpdatedSubject = new Subject<void>();
  public profileUpdated$ = this.profileUpdatedSubject.asObservable();

  /** Última URL de avatar conhecida (do perfil ou do upload), para fallback quando a API não retorna no GET /users/me */
  private lastAvatarUrl: string | null = null;

  constructor(private http: HttpClient, private router: Router) {}

  /** Retorna a última URL de avatar conhecida (útil quando GET /users/me não devolve avatarUrl). */
  getLastAvatarUrl(): string | null {
    return this.lastAvatarUrl;
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(
        `${environment.apiUrl}/auth/sign-in`,
        credentials
      )
      .pipe(
        tap((response) => {
          this.setToken(response.token);
          if (response.refreshToken) {
            this.setRefreshToken(response.refreshToken);
          }
          this.setUser(response.user);
          this.currentUserSubject.next(response.user);
          // Salvar nome da empresa se retornado no user do login
          if (response.user?.companyName) {
            this.setCompanyName(response.user.companyName);
          }
        })
      );
  }

  register(data: RegisterRequest): Observable<RegisterResponse> {
    return this.http
      .post<RegisterResponse>(
        `${environment.apiUrl}/auth/sign-up`,
        data
      )
      .pipe(
        tap((response) => {
          this.setToken(response.token);
          this.setUser(response.user);
          this.currentUserSubject.next(response.user);
          
          // Salvar nome da empresa do registro
          if (response.companyName) {
            this.setCompanyName(response.companyName);
          } else if (response.user?.companyName) {
            this.setCompanyName(response.user.companyName);
          }
        })
      );
  }

  forgotPassword(data: ForgotPasswordRequest): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/auth/forgot-password/request`,
      data
    );
  }

  confirmForgotPassword(data: ForgotPasswordConfirmRequest): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}/auth/forgot-password/confirm`,
      data
    );
  }

  logout(): void {
    this.clearStorage();
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  getCompanyName(): string | null {
    return localStorage.getItem(this.COMPANY_NAME_KEY);
  }

  private setCompanyName(companyName: string): void {
    localStorage.setItem(this.COMPANY_NAME_KEY, companyName);
  }

  refreshToken(): Observable<LoginResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    return this.http
      .post<LoginResponse>(
        `${environment.apiUrl}/auth/refresh-token`,
        {
          refreshToken,
        }
      )
      .pipe(
        tap((response) => {
          this.setToken(response.token);
          if (response.refreshToken) {
            this.setRefreshToken(response.refreshToken);
          }
          
          this.setUser(response.user);
          this.currentUserSubject.next(response.user);
          // Salvar nome da empresa se retornado no user
          if (response.user?.companyName) {
            this.setCompanyName(response.user.companyName);
          }
        })
      );
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private setRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  private getUserFromStorage(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (!userStr) {
      return null;
    }
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  private clearStorage(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.COMPANY_NAME_KEY);
    this.lastAvatarUrl = null;
  }

  /**
   * Busca os dados completos do perfil do usuário atual
   */
  getMyProfile(): Observable<UserProfile> {
    return this.http
      .get<any>(
        `${environment.apiUrl}/users/me`
      )
      .pipe(
        map((profile: UserProfile) => {
          // Atualizar nome da empresa se retornado no perfil
          if (profile.companyName) {
            this.setCompanyName(profile.companyName);
          }
          // Guardar avatar para fallback (API pode retornar AvatarUrl em PascalCase)
          const avatarUrl = profile.avatarUrl ?? (profile as any).AvatarUrl;
          if (avatarUrl) {
            this.lastAvatarUrl = avatarUrl;
          }
          return { ...profile, avatarUrl: avatarUrl || profile.avatarUrl };
        })
      );
  }

  /**
   * Atualiza os dados do perfil do usuário atual
   */
  updateMyProfile(data: UpdateUserProfileRequest): Observable<UserProfile> {
    return this.http
      .put<UserProfile>(
        `${environment.apiUrl}/users/me`,
        data
      )
      .pipe(
        tap((response) => {
          // Atualizar o usuário no storage se a resposta incluir dados básicos
          if (response) {
            const currentUser = this.getCurrentUser();
            if (currentUser) {
              const updatedUser: User = {
                ...currentUser,
                name: response.name,
              };
              this.setUser(updatedUser);
              this.currentUserSubject.next(updatedUser);
            }
          }
        })
      );
  }

  /**
   * Faz upload do avatar do usuário atual
   * @param file Arquivo de imagem a ser enviado
   * @returns Observable com a resposta contendo avatarUrl
   */
  uploadAvatar(file: File): Observable<{ avatarUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<{ avatarUrl: string }>(
        `${environment.apiUrl}/users/avatar`,
        formData
      )
      .pipe(
        tap((response) => {
          if (response?.avatarUrl) {
            this.lastAvatarUrl = response.avatarUrl;
          }
          this.profileUpdatedSubject.next();
        })
      );
  }
}
